import * as React from "react";
import { connect } from "react-redux";
import get from "lodash/get";
import defaultTo from "lodash/defaultTo";

import {
  clearWidgetFormErrors,
  disableWidgetConfig,
  setScreenName,
  widgetEventTrack,
} from "../../actions";
import "./Screen.css";
import { StoreState } from "../../reducers";
import {
  getIsOverlay,
  getWidgetConfigId,
  getWidgetId,
} from "../../reducers/methods";
import {
  isEditMode,
  onClickSendToMerchant,
} from "../../utils/sendClickToMerchant";
import { RouterProps } from "../Router/Router";
import { RouterRule } from "../../types";
import { flattenObject } from "../../utils/flattenObject";
import { generateErrorId } from "../../utils/string";
import { createFocusTrap } from "focus-trap";
import { WIDGET_CONTAINER_SELECTOR } from "../../constants";

export function isMobileDevice(config: { [index: string]: any }) {
  const mobileWidth = config.settings.widgetSizes.mobile.width;
  const mobileWidthIsInPixels = mobileWidth.includes("px");
  const desktopWidth = config.settings.widgetSizes.desktop.width;
  const desktopWidthIsInPixels = desktopWidth.includes("px");

  if (mobileWidthIsInPixels) {
    return window.innerWidth + "px" === mobileWidth;
  } else if (desktopWidthIsInPixels) {
    return window.innerWidth + "px" !== desktopWidth;
  } else {
    return window.innerWidth < 600;
  }
}

export interface ScreenProps {
  position?: string;
  closeButton?: string;
  closeButtonStyle?: React.CSSProperties;
  config: {
    [index: string]: { [index: string]: string | number } & {
      router?: RouterProps & { renderDefault: Function };
    };
  };
  friendbuyLogoColor: "white" | "black";
  data: any;
  name: string;
  configName: string;
  style?: React.CSSProperties;
  scrollToTop?: boolean;
  scrollOffset?: number;
  parentStyle?: React.CSSProperties;
  children?: React.ReactChildren;
  duration: number;
  transitionStyles: { [index: string]: { [index: string]: string } };
  defaultStyle: { [index: string]: string };
  disableWidgetConfig: typeof disableWidgetConfig;
  isOverlay: boolean;
  mode?: "cover" | "contain" | "repeat" | "stretch";
  parentMode?: "cover" | "contain" | "repeat" | "stretch";
  clearWidgetFormErrors: typeof clearWidgetFormErrors;
  setScreenName: typeof setScreenName;
  widgetEventTrack: typeof widgetEventTrack;
  widgetConfigId: string;
  widgetId: string;
}

// tslint:disable-next-line:no-any
export class Screen extends React.PureComponent<ScreenProps> {
  preventDefaultFormAction = (e: React.MouseEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  closeWidget = () => {
    const { disableWidgetConfig, widgetConfigId } = this.props;
    disableWidgetConfig(widgetConfigId);
  };

  renderCloseButton() {
    const closeButton =
      this.props.closeButton ||
      "https://static-friendbuy-com.s3.amazonaws.com/widget/friendbuy/Widget/CloseOut-glow.svg";
    const hasOverlay = get(this.props, "config.settings.hasOverlay", "no");
    const isCloseButtonVisible = hasOverlay === "yes";

    if (isCloseButtonVisible) {
      return (
        <button
          // Without this it defaults to type="submit" inside the screen's
          // form, so implicit submission could click it and close the widget.
          type="button"
          className="Close-widget-wrapper"
          style={get(this.props, "closeButtonStyle", {
            width: "25px",
            height: "25px",
            top: "5px",
            right: "12px",
          })}
          onClick={this.closeWidget}
          tabIndex={0}
          aria-label="Close Widget"
        >
          <img
            style={{
              width: get(this.props, "closeButtonStyle.width", "25px"),
              height: get(this.props, "closeButtonStyle.height", "25px"),
            }}
            className="Close-widget-button"
            src={closeButton}
          />
        </button>
      );
    } else {
      return null;
    }
  }

  /**
   * Calculates the appropriate height for the widget container based on content
   * and configuration
   * @returns {string} The calculated height with units (px)
   */
  getBottomMostPosition = () => {
    const screenHeight = String(get(this.props, "style.height", "0"));
    const borderTopWidth = String(get(this.props, "style.borderTopWidth", "0"));
    const borderBottomWidth = String(
      get(this.props, "style.borderBottomWidth", "0")
    );

    const displayHeight = this.getDisplayHeight();
    const bottomMostPosition = this.calculateBottomMostPosition();
    const displayHeightValue = this.cleanNumber(displayHeight);
    const screenHeightValue = this.cleanNumber(screenHeight);
    const borderTopWidthValue = this.cleanNumber(borderTopWidth);
    const borderBottomWidthValue = this.cleanNumber(borderBottomWidth);

    // If displayHeight contains "%", or content is taller than display height,
    // return the bottomMostPosition in pixels (with some padding).
    if (
      displayHeight.includes("%") ||
      bottomMostPosition > displayHeightValue
    ) {
      return this.getPixelHeight({ height: bottomMostPosition, padding: 40 });
    }

    // Use display height for percentage-based screen heights
    if (screenHeight.includes("%")) {
      return this.getPixelHeight({ height: displayHeightValue });
    }

    return this.getPixelHeight({
      // Calculate the height of the screen by subtracting the border top and
      // bottom widths from the screen height (account for border-box model).
      height: screenHeightValue - borderTopWidthValue - borderBottomWidthValue,
    });
  };

  renderContent = () => {
    const { children, configName } = this.props;

    if (this.isDuplicateError()) {
      return this.renderDuplicateOverlay();
    }

    return (
      <form
        onSubmit={this.preventDefaultFormAction}
        onClick={onClickSendToMerchant(configName)}
      >
        {children}
        {this.renderCloseButton()}
      </form>
    );
  };

  renderScreen = () => {
    const { config, mode, parentMode } = this.props;

    const style: React.CSSProperties = {
      ...this.props.style,
      overflow: "auto",
    };

    const parentStyle: React.CSSProperties = {
      ...this.props.parentStyle,
      display: "flex",
      flexDirection: "row",
    };
    const bottomMostPosition = this.getBottomMostPosition();
    const isLandingPage =
      get(this.props, "config.settings.designType") === "landingPage";

    if (isMobileDevice(config)) {
      return (
        <div
          id="Widget-Screen"
          style={{ ...style, margin: "0 auto" }}
          className={`Screen-container Screen-mode-${mode || "default"}`}
          data-iframeresizer-height-calc-hint="1"
        >
          {this.renderSuccessNotification()}
          {this.renderError()}
          <div
            style={{
              minHeight: bottomMostPosition,
            }}
            className="Min-height-container"
          >
            {this.renderContent()}
          </div>
        </div>
      );
    } else {
      if (isLandingPage) {
        return (
          <div
            data-iframeresizer-height-calc-hint="1"
            style={{
              ...parentStyle,
              // This ensures the parent container for landing page widgets is
              // the same height as the display height set in the widget builder
              // editor.
              height: this.getDisplayHeight(),
            }}
            className={`Parent-screen-container Screen-mode-${
              parentMode || "default"
            }`}
          >
            {this.renderSuccessNotification()}
            {this.renderError()}
            <div
              id="Widget-Screen"
              style={{ ...style }}
              className={`Screen-container Screen-mode-${mode || "default"}`}
            >
              <div
                style={{ minHeight: bottomMostPosition, overflow: "none" }}
                className="Min-height-container"
              >
                {this.renderContent()}
              </div>
            </div>
          </div>
        );
      }

      return (
        <div
          id="Widget-Screen"
          style={style}
          className={`Screen-container Screen-mode-${mode || "default"}`}
          data-iframeresizer-height-calc-hint="1"
        >
          {this.renderSuccessNotification()}
          {this.renderError()}
          <div
            style={{ minHeight: bottomMostPosition, overflow: "none" }}
            className="Min-height-container"
          >
            {this.renderContent()}
          </div>
        </div>
      );
    }
  };

  testRule(rule: RouterRule): boolean {
    const { data } = this.props;

    switch (rule.operator) {
      case "is":
        return get(data, rule.attribute) === rule.value;
      case "isDefined":
        return get(data, rule.attribute);
      case "isNotDefined":
        return !get(data, rule.attribute);
      default:
        return false;
    }
  }

  getScreen(): string | void {
    const { config, configName } = this.props;
    const router = get(config[configName], "router");

    if (!router || !router.rules) {
      return undefined;
    }

    for (const rule of router.rules) {
      if (this.testRule(rule)) {
        return rule.screen;
      }
    }
  }

  isDuplicateError = () => {
    // If this is an Earning Events widget, let the template handle the duplicate UI instead of the global SDK overlay.
    const designType = get(this.props, "config.settings.designType", "");
    if (
      designType === "earningEvents" ||
      this.props.configName?.toLowerCase().includes("earningevents")
    ) {
      return false;
    }

    // Check if duplicate is flagged organically via the receipt payload
    const receipt =
      get(this.props, "data.receipt") ||
      get(this.props, "share.receipt") ||
      get(this.props, "data");
    if (receipt && receipt.created === false) {
      return true;
    }

    // Checking validation errors if mock throws a generic error (safeguard)
    const errors = get(this.props, "formErrors", {});
    const flattenedErrors = flattenObject(errors);
    return Object.values(flattenedErrors).some(
      (err) =>
        typeof err === "string" && err.toLowerCase().includes("already exists")
    );
  };

  renderDuplicateOverlay = () => {
    return (
      <div className="Widget-duplicate-receipt-card">
        <div className="Widget-duplicate-receipt-icon-wrapper">
          <svg
            className="Widget-duplicate-receipt-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 9V14M12 17.5V18M21.536 15.549L15.342 4.453C14.154 2.327 11.846 2.327 10.658 4.453L4.464 15.549C3.276 17.675 4.43 20 6.806 20H19.194C21.57 20 22.724 17.675 21.536 15.549Z"
              stroke="#E48538"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2 className="Widget-duplicate-receipt-title">Duplicate Receipt</h2>
        <div style={{ marginTop: "12px", padding: "0 10px" }}>
          <p className="Widget-duplicate-receipt-text">
            This receipt has already been uploaded. No additional points were
            awarded. Please upload a different receipt to earn rewards.
          </p>
        </div>
        <div style={{ marginTop: "24px" }}>
          <button
            type="button"
            className="Widget-duplicate-receipt-btn"
            onClick={this.props.clearWidgetFormErrors}
          >
            Upload Another
          </button>
        </div>
      </div>
    );
  };

  renderError = () => {
    if (this.isDuplicateError()) {
      return null;
    }

    const customErrorStyle = get(
      this.props,
      "config.settings.customErrorStyle",
      {}
    );
    const errors = get(this.props, "formErrors", {});
    const flattenedErrors = flattenObject(errors);
    const hasErrors = Object.values(flattenedErrors).length > 0;
    const containerClassName = hasErrors
      ? "Widget-form-errors-container Widget-form-errors-container-expanded"
      : "Widget-form-errors-container";
    const closeButtonClassName = hasErrors
      ? "Widget-form-errors-close Widget-form-errors-close-visible"
      : "Widget-form-errors-close";

    return (
      <div
        id="WidgetFormErrors"
        className={containerClassName}
        style={customErrorStyle}
      >
        <div className="Widget-form-errors-inner-container">
          <div
            className={closeButtonClassName}
            onClick={this.props.clearWidgetFormErrors}
            tabIndex={0}
            role="button"
            aria-label="Close error messages"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M10.5 1.5L1.5 10.5" stroke="white" strokeWidth="1.5" />
              <path d="M1.5 1.5L10.5 10.5" stroke="white" strokeWidth="1.5" />
            </svg>
          </div>
          <div className="Widget-form-errors" role="alert">
            {hasErrors && (
              <h4 className="screen-reader-only">
                Please fix the following errors:
              </h4>
            )}
            {Object.entries(flattenedErrors).map(
              ([key, value]: [string, string]) => (
                <div
                  key={key}
                  id={generateErrorId(key)}
                  className="Widget-form-error"
                >
                  {value}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  renderSuccessNotification = () => {
    // If it's a duplicate error and the overlay is replacing the form,
    // do not show the success/warning banner.
    if (this.isDuplicateError()) {
      return null;
    }

    const customSuccessStyle = get(
      this.props,
      "config.settings.customSuccessStyle",
      {}
    );

    return (
      <div
        id="widget-success-notification"
        className="Widget-success-container"
        style={customSuccessStyle}
      >
        <div className="Widget-success-inner-container">
          <div className="Widget-success" id="widget-success-notification-text">
            Thanks for sharing!
          </div>
        </div>
      </div>
    );
  };

  componentDidMount() {
    const { scrollToTop = false, scrollOffset = 0 } = this.props;

    // If scrollToTop is enabled and we're not in edit mode, send a message
    // to the parent iframe to scroll to the top with the specified offset.
    // This allows us to scroll to the top of the iframe when the Screen is
    // changed, but not when the widget is in edit mode (WidgetBuilder).
    if (scrollToTop && !isEditMode()) {
      if ("parentIframe" in (window as any)) {
        (window as any).parentIframe.sendMessage({
          type: "scroll_to_top",
          scrollOffset: +scrollOffset,
        });
      }
    }

    this.props.clearWidgetFormErrors();
    const screen = this.getScreen();
    if (screen && !this.props.config.__editMode__) {
      this.props.setScreenName(screen);
    }

    if (!isEditMode()) {
      this.props.widgetEventTrack({
        type: "widget_event",
        name: "widget_event",
        action: "screen_view",
        screenName: get(this.props.config, `${this.props.configName}.label`),
      });
    }

    if (this.props.isOverlay) {
      const focusTrap = createFocusTrap(WIDGET_CONTAINER_SELECTOR);
      focusTrap.activate();
    }
  }

  render() {
    const screen = this.getScreen();

    if (screen && !this.props.config.__editMode__) {
      return null;
    }

    return this.renderScreen();
  }

  /**
   * Gets the configured display height based on device type
   * @returns {string} The height value with units
   * @private
   */
  private getDisplayHeight(): string {
    const { config } = this.props;

    return String(
      isMobileDevice(config)
        ? get(config, "settings.widgetSizes.mobile.height", "0")
        : get(config, "settings.widgetSizes.desktop.height", "0")
    );
  }

  /**
   * Calculates the position of the bottom-most component in the widget
   * @returns {number} The bottom position in pixels
   * @private
   */
  private calculateBottomMostPosition(): number {
    const bottomMostComponent = this.getBottomMostComponent();

    if (!bottomMostComponent[0]) {
      return 0;
    }

    return (
      this.getComponentTop(bottomMostComponent[0]) +
      this.getComponentHeight(bottomMostComponent[0])
    );
  }

  /**
   * Finds the component positioned lowest in the widget
   * @returns {any[]} Sorted array of components
   * @private
   */
  private getBottomMostComponent(): any[] {
    return React.Children.toArray(get(this.props, "children", []))
      .slice()
      .sort(
        (componentA: any, componentB: any) =>
          this.getComponentTop(componentB) - this.getComponentTop(componentA)
      );
  }

  private getComponentHeight(component: any): number {
    return Number(
      defaultTo(get(component, "props.style.height"), "0")
        .replace("px", "")
        .replace(/.*%/, "")
    );
  }

  private getComponentTop(component: any): number {
    return Number(
      defaultTo(get(component, "props.style.top"), "0")
        .replace("px", "")
        .replace(/.*%/, "")
    );
  }

  /**
   * Removes unit suffixes (px, %) from a string and converts to number
   * @param {string} str - The string value with units
   * @returns {number} The cleaned number value
   * @private
   */
  private cleanNumber(str: string): number {
    return Number(str.replace(/px|%/g, ""));
  }

  /**
   * Converts a height value to a pixel string with optional padding
   * @param {Object} params - The parameters
   * @param {number} params.height - The base height value
   * @param {number} params.padding - Additional padding to add (default: 0)
   * @returns {string} The height in pixel units
   * @private
   */
  private getPixelHeight({
    height,
    padding = 0,
  }: {
    height: number;
    padding?: number;
  }): string {
    return `${height + padding}px`;
  }
}

// tslint:disable-next-line:no-any
const mapStateToProps = (state: StoreState, ownProps: ScreenProps): any => {
  return {
    isOverlay: getIsOverlay(state),
    widgetConfigId: getWidgetConfigId(state),
    widgetId: getWidgetId(state),
  };
};

export const ScreenWrapped = connect(mapStateToProps, {
  clearWidgetFormErrors,
  disableWidgetConfig,
  widgetEventTrack,
})(Screen);
